<template>
  <ToolShell
    tool-name="Payment fee calculator"
    h1="Payment processing fee calculator."
    lede="Enter the invoice amount and how the client pays — see the fee, the effective rate, and what you keep, using Stripe's published US rates or your own provider's rate. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Stripe rates checked Nov 1, 2026']"
    form-title="Your numbers"
    :upsell-points="[
      'Invoices and payment links collect online on every plan — from $29/month',
      'Payment plans split big invoices from Specialist ($149, 10 seats)',
      'Automated reminders follow up so you don’t have to',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The fee on one invoice, calculated once',
      'You pick the method and chase the payment yourself',
      'No running total of what fees cost you each month',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Invoices and payment links collect online — every plan from $29/month',
      'Payment plans split large invoices from Specialist ($149, 10 seats)',
      'Follow-up reminders run automatically with automations',
      'Every payment lands on the client record — status always visible',
    ]"
    slug="/tools/payment-processing-fee-calculator"
    :meta-description="metaDescription"
    doc-brand="Fee calculator"
    :doc-subtitle="invoiceSummary"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Invoice amount ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.amount" type="number" min="0" step="0.01" placeholder="1000" />
        </div>
      </label>

      <div class="ta-field">
        <span id="fee-method-label">Payment method</span>
        <ToolDropdown
          v-model="form.method"
          :options="methodOptions"
          aria-label="Payment method"
          labelledby="fee-method-label"
        />
      </div>

      <label class="ta-field">
        <span>Invoices per month (optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.volume" type="number" min="1" max="10000" step="1" placeholder="1" />
        </div>
      </label>

      <label class="ta-field">
        <span>Your provider's rate, % (optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 12.5l9-9M5 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM11 12.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.customPct" type="number" min="0" max="20" step="0.01" placeholder="2.9" />
        </div>
      </label>

      <label class="ta-field">
        <span>Your provider's fixed fee per payment, $ (optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.customFixed" type="number" min="0" max="10" step="0.01" placeholder="0.30" />
        </div>
      </label>

      <p class="ta-note">
        Card vs ACH across payment sizes, with the source data:
        <NuxtLink :to="localePath('/research/payment-processing-cost-benchmarks')">our payment processing cost benchmarks</NuxtLink>.
        Selling through a marketplace instead? The
        <NuxtLink :to="localePath('/tools/upwork-vs-fiverr-fee-calculator')">Upwork vs Fiverr fee calculator</NuxtLink> shows what each platform keeps.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Invoice amount</span><strong>{{ money(amount) }}</strong></div>
        <div><span>Method</span><strong>{{ methodLabel }}</strong></div>
        <div><span>Fee · {{ result.name }}</span><strong>{{ money(result.fee) }}</strong></div>
        <div><span>Effective rate</span><strong>{{ effectiveRate }}%</strong></div>
        <div><span>You receive</span><strong>{{ money(youReceive) }}</strong></div>
        <div v-if="volume > 1"><span>Fees per month · {{ volume }} invoices</span><strong>{{ money(monthlyFees) }}</strong></div>
        <div v-if="volume > 1"><span>You keep per month</span><strong>{{ money(monthlyKeep) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>The same invoice</th><th>Fee</th><th>You receive</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>
                {{ row.name }}
                <span class="ta-rate">{{ row.rateLabel }}</span>
              </td>
              <td class="ta-num">{{ row.fee === null ? "Enter a rate" : money(row.fee) }}</td>
              <td class="ta-num">{{ row.fee === null ? "—" : money(amount - row.fee) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>You keep · {{ result.name }}</span>
        <strong>{{ money(youReceive) }}</strong>
      </div>

      <p class="ta-doc-note">
        Stripe's published standard US rates from
        <a href="https://stripe.com/pricing" target="_blank" rel="noopener">stripe.com/pricing</a>, checked November 1, 2026,
        for domestic cards and ACH Direct Debit. International cards and currency conversion cost more, and your account's
        rates may differ. These are processing rates only: they don't include any fees from the invoicing software you use.
      </p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the invoice</h2><p>The invoice amount, and how many like it you send each month.</p></div></div>
      <div><span>02</span><div><h2>Pick the method</h2><p>Online card, ACH bank transfer, or a card keyed in by hand.</p></div></div>
      <div><span>03</span><div><h2>See what you keep</h2><p>The fee and effective rate with Stripe's published rate, and with your own provider's rate if you enter it.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Payment fee FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/payment-processing-fee-calculator" title="Payment fee calculator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { paymentFee, paymentMethods } from "~/data/research/payment-fees.mjs";

const { localePath } = useWorkspaceLocale();

const form = reactive({ amount: 1000, method: "card", volume: 1, customPct: null, customFixed: null });

const methodOptions = [
  { value: "card", label: "Card — online (payment link)" },
  { value: "ach", label: "ACH bank transfer" },
  { value: "keyed", label: "Card — keyed in" },
];

// Stripe's published standard US rates (stripe.com/pricing, checked 2026-11-01): online
// domestic cards and ACH Direct Debit come from the site's research set
// (data/research/payment-fees.mjs: stripe-card, stripe-ach); a manually entered card adds
// 0.5% to the card rate. No other provider is named here: the visitor enters their own rate.
const stripeCard = paymentMethods.find((method) => method.id === "stripe-card");
const KEYED_SURCHARGE_PCT = 0.5;
const stripeRateLabel = {
  card: "2.9% + 30¢ · domestic cards",
  ach: "0.8% · $5.00 cap",
  keyed: "2.9% + 0.5% + 30¢ · manually entered",
};

const cents = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const amount = computed(() => {
  const value = Number(form.amount);
  return Number.isFinite(value) && value > 0 ? Math.min(value, 1000000) : 0;
});

const volume = computed(() =>
  Math.max(1, Math.min(10000, Math.round(Number(form.volume) || 1))),
);

const stripeFee = (method, value) => {
  if (value < 1) return 0;
  if (method === "card") return paymentFee("stripe-card", value);
  if (method === "ach") return paymentFee("stripe-ach", value);
  return cents((cents(value) * (stripeCard.rate + KEYED_SURCHARGE_PCT)) / 100 + stripeCard.fixed);
};

// An empty rate field means "not entered", so the summary stays on Stripe's published rate.
const customPct = computed(() => {
  if (form.customPct === null || form.customPct === "") return null;
  const value = Number(form.customPct);
  return Number.isFinite(value) && value >= 0 ? Math.min(value, 20) : null;
});
const customFixed = computed(() => {
  const value = Number(form.customFixed);
  return Number.isFinite(value) && value > 0 ? Math.min(value, 10) : 0;
});

const customFee = computed(() => {
  if (customPct.value === null) return null;
  if (amount.value < 1) return 0;
  return cents((cents(amount.value) * customPct.value) / 100 + customFixed.value);
});

const rows = computed(() => [
  {
    id: "stripe",
    name: "Stripe published rate",
    rateLabel: stripeRateLabel[form.method],
    fee: stripeFee(form.method, amount.value),
  },
  {
    id: "custom",
    name: "Your provider's rate",
    rateLabel:
      customPct.value === null
        ? "Enter your rate on the left"
        : `${customPct.value}%${customFixed.value ? ` + ${money(customFixed.value)}` : ""}`,
    fee: customFee.value,
  },
]);

// The summary follows your own rate once you enter it, and Stripe's published rate until then.
const result = computed(() => (customFee.value === null ? rows.value[0] : rows.value[1]));

const youReceive = computed(() => cents(amount.value - result.value.fee));
const effectiveRate = computed(() =>
  amount.value > 0 ? cents((result.value.fee / amount.value) * 100) : 0,
);
const monthlyFees = computed(() => cents(result.value.fee * volume.value));
const monthlyKeep = computed(() => cents(amount.value * volume.value - monthlyFees.value));

const methodLabel = computed(
  () => methodOptions.find((option) => option.value === form.method)?.label || "",
);

const invoiceSummary = computed(() => `${money(amount.value)} invoice · ${methodLabel.value}`);

const faq = [
  {
    q: "Is ACH cheaper than taking a card?",
    a: "On Stripe's published US rates, yes — at any invoice size. A $1,000 invoice costs $29.30 by card (2.9% + 30¢) and $5.00 by ACH Direct Debit (0.8% with a $5 cap). The trade-off: bank transfers can take longer to reach your account, so check your provider's payout schedule.",
  },
  {
    q: "What does the effective rate mean?",
    a: "The fee as a share of the invoice: fee ÷ invoice amount × 100. The fixed per-transaction part makes small invoices cost more per dollar — a $50 invoice at 2.9% + 30¢ works out to 3.5%, not 2.9%.",
  },
  {
    q: "Why enter my own provider's rate?",
    a: "Stripe's published rate is a common starting point, but your processor may charge a different percentage or fixed fee, and some accounts get custom pricing. Enter the rate from your own statement or pricing page and the calculator shows your fee and what you keep next to Stripe's published rate.",
  },
  {
    q: "How do I pay less in processing fees?",
    a: "Where your provider supports it, offer ACH on large invoices, keep cards for when speed matters, and avoid keying cards in by hand.",
  },
];

const metaTitle = "Payment Processing Fee Calculator | Workspace369";
const metaDescription =
  "See what card and ACH fees cost per invoice with Stripe's published US rates or your own provider's rate, and what you keep. Free, instant, no signup.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.ta-rate {
  display: block;
  margin-top: 0.1rem;
  color: #9aa3ad;
  font-size: 0.72rem;
  font-weight: 600;
}

.ta-doc-note a {
  color: inherit;
  text-decoration: underline;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .ta-rate {
  color: #94a3b8;
}
</style>
